/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useEffect, useState } from "react";

//@Packages
import { Button } from "@forge/common";
import { useTranslation } from "react-i18next";

//@Components
import CustomizedTable from "../customizedtable";

//@Models
import { ConnectionDetails, ActiveConnectionListHeaderProps } from "../../../models/remoteConnect";

//@Utils
import { LOCALE_CONST } from "../../../utils/constants";
import { activeConnectionConnected } from "../../../utils/staticdata";
import ConnectConfirmModal from "../Modal/ConnectConfirmModal";

//@Styles
import "./styles.scss";
import { useGetActiveConnectionUserDetailsQuery } from "../../../services/remote-connect-api";
import { formatActiveConnectionsTimestamp, gatewayErrorStatusMessages, getTimeDifference } from "../../../utils/utility";

const ActiveConnections = () => {
  const { t } = useTranslation();
  const convertFn = (val: string) => t(val);
  const [showConfirmationModal, setShowConfirmationModal] = useState({
    isOpen: false,
    clientName: ""
  });
  const [filteredData, setFilteredData] = useState<ConnectionDetails[]>([]);
  const [pageChange, setPageChange] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState<string>("");
  const getDeviceID = () => sessionStorage.getItem("linkedDevicesSystemGuid") || "";

  // Fetch active devices list using the custom hook
  const { data: getActiveConList,
    isLoading: isActiveConListLoading,
    isError: isActiveConListError,
    error: activeConListError,
    isFetching: isActiveConListFetching
  } = useGetActiveConnectionUserDetailsQuery(
    { gatewayId: getDeviceID() }
  );

  const [filterHeaders, setFilterHeaders] = useState<ActiveConnectionListHeaderProps[]>(activeConnectionConnected);

  // Initialize the table headers and filter headers
  useEffect(() => {
    if (getActiveConList?.connections) {
      setFilteredData(getActiveConList?.connections);
    }
  }, [getActiveConList]);

  // Map table content
  const formatActiveConnectionData = (data: ConnectionDetails[]) => {
    const formatCellContent = (value: any) => {
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
      }
      else if (value === null || value === undefined) {
        return value || "-";
      }
      return value || "-";
    };

    return data.map((item, index) => ({
      id: (index + 1).toString(),
      cell: filterHeaders?.map((header) => {
        let cellContent = <span>{formatCellContent(item[header.id])}</span>;
        //remove this action header column if not needed
        // if (header.id === "actions") {
        //   cellContent = (
        //     <div className="action-button-container">
        //       <Button
        //         aria-label="Settings"
        //         data-testid="edit-device"
        //         className="action-button-disconnect"
        //         variant="quiet-secondary"
        //         onClick={() => {
        //           setShowConfirmationModal({
        //             isOpen: true,
        //             clientName: item.user_name
        //           });
        //         }}
        //       >
        //         {t(LOCALE_CONST.disconnect)}
        //       </Button>
        //     </div>
        //   );
        // }
         if (header.id === "connectedAt") {
          cellContent = (
            <span>{formatActiveConnectionsTimestamp(item.connected_at)}</span>
          );
        }
        else if (header.id === "duration") {
          cellContent = (
            <span>{getTimeDifference(item.connected_at)}</span>
          );
        }
        return { content: cellContent };
      }),
    }));
  };

  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();

  // Function to get paginated data based on current page and page size
  const getPaginatedData = () => {
    const startIndex = (pageChange - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  };


  // Handlers
  const pageChangeHandler = (page: number) => {
    setPageChange(page);
  };

  const pageSizeChangeHandler = (pageSize: number) => {
    setPageSize(pageSize);
    setPageChange(1); // Reset to first page on page size change
  };

  const sortHandler = (dir: string, id: string) => {
    console.log("Sort changed", dir, id);
  };

  const searchOnChangeHandler = (searchValue: string) => {
    const searchValueLower = normalizeSpaces(searchValue);
    setSearchValue(searchValueLower);

    const filtered = getActiveConList?.connections?.filter((data: ConnectionDetails) =>
      Object.values(data || {}).some((value) =>
        value != null && value.toString().toLowerCase().startsWith(searchValueLower)
      )
    );


    // If no matches, set filteredData to an empty array
    setFilteredData(filtered?.length > 0 ? filtered : []);
    setPageChange(1); // Reset to the first page
    console.log("Filtered data:", filtered);

  };

  const countNumberOfDevices = (data: ConnectionDetails[]) => {
    // Sum all the numberOfDevices values in the array
    if (!data || data.length === 0) return 0;

    return data.reduce((total, item) => total + (Number(item.number_of_devices) || 0), 0);
  };

  return (
    <div className="active-connections-remote-container">
      <CustomizedTable
        title={t(LOCALE_CONST.activeConnections)}
        // filterIconClick={() => {}}
        slotLeft={{ status: "Authenticated", count: countNumberOfDevices(getActiveConList?.connections || []) }}
        searchOnChange={searchOnChangeHandler}
        searchValue={searchValue}
        onSearchClear={() => {
          setSearchValue("");
          setFilteredData(getActiveConList.connections || []);
          setPageChange(1);
        }}
        onExportAction={() => { }}
        tableData={filteredData} tableHeaderData={filterHeaders}
        tableContentData={formatActiveConnectionData(getPaginatedData())} // Use the mapped content here
        isSearch={false}
        onPageChange={pageChangeHandler}
        onPageSizeChange={pageSizeChangeHandler}
        onUpdateSort={sortHandler}
        initialPage={
          pageChange
        }
        maxPages={
          filteredData.length > 0 ? Math.ceil(filteredData.length / pageSize) : 0
        }
        pageSize={pageSize}
        totalItems={filteredData.length}
        totalPages={
          filteredData.length > 0 ? Math.ceil(filteredData.length / pageSize) : 0
        }
        isPaginationEnabled={true}
        isLoading={isActiveConListLoading || isActiveConListFetching}
        isError={isActiveConListError}
        errorText={
          activeConListError && "status" in activeConListError
            ? gatewayErrorStatusMessages(
              convertFn,
              t(LOCALE_CONST.unknownError),
              activeConListError.status,
              {
                400: t(LOCALE_CONST.badRequest_ac_400),
                401: t(LOCALE_CONST.unauthorized_ac_401),
                403: t(LOCALE_CONST.forbidden_ac_403),
                404: t(LOCALE_CONST.notFound_ac_404),
                500: t(LOCALE_CONST.internalServerError_ac_500),

              }
            )
            : undefined
        }
      />
      {/* Confirmation Modal */}
      <ConnectConfirmModal
        isOpen={showConfirmationModal.isOpen}
        onClose={() => setShowConfirmationModal({
          isOpen: false,
          clientName: ""
        })}
        onConfirm={() => {
          setShowConfirmationModal({
            isOpen: false,
            clientName: ""
          });
        }}
        title="Confirm Disconnect"
        content={`${showConfirmationModal.clientName} will be disconnected`}
        footerLeftButtonText="Cancel"
        footerRightButtonText="Confirm"
        confirmButtonVariant="destructive-primary"
      />
    </div>
  );
};

export default ActiveConnections;